/**
 * @file Registry-aware grouping metadata and config helpers for parent boards.
 */

import { appData } from "../../app/state/state.js";
import {
  getBoardRegistryEntry,
  getBoardRegistryEntryByItemType,
} from "./boardRegistry.js";

const GROUP_LABELS = Object.freeze({
  status: "Status",
  date: "Date",
  type: "Type",
  urgency: "Urgency",
  assignee: "Assignee",
  provider: "Provider",
  lab: "Lab",
  technician: "Technician",
});

const DATE_GROUPS_BY_MODE = Object.freeze({
  daily: Object.freeze([
    Object.freeze({ id: "overdue", title: "Overdue" }),
    Object.freeze({ id: "today", title: "Due Today" }),
    Object.freeze({ id: "tomorrow", title: "Tomorrow" }),
    Object.freeze({ id: "later", title: "Due Later" }),
  ]),
  weekly: Object.freeze([
    Object.freeze({ id: "overdue", title: "Overdue" }),
    Object.freeze({ id: "thisWeek", title: "Due This Week" }),
    Object.freeze({ id: "nextWeek", title: "Due Next Week" }),
    Object.freeze({ id: "later", title: "Due Later" }),
  ]),
  monthly: Object.freeze([
    Object.freeze({ id: "overdue", title: "Overdue" }),
    Object.freeze({ id: "thisMonth", title: "Due This Month" }),
    Object.freeze({ id: "nextMonth", title: "Due Next Month" }),
    Object.freeze({ id: "later", title: "Due Later" }),
  ]),
});

/**
 * Returns supported grouping keys for a parent-board view.
 * @param {string} viewKey - Parent-board view key.
 * @returns {Array<string>} Supported group keys in display order.
 */
export function getBoardGroupOptions(viewKey) {
  return [
    ...(getBoardRegistryEntry(viewKey)?.grouping?.supportedGroupKeys || []),
  ];
}

/**
 * Returns the display label for a group key.
 * @param {string} groupKey - Group key.
 * @returns {string} Display label.
 */
export function getBoardGroupLabel(groupKey) {
  return GROUP_LABELS[groupKey] || groupKey;
}

/**
 * Returns date group definitions for the requested date grouping mode.
 * @param {string} mode - Date grouping mode.
 * @returns {Array<object>} Date group definitions in display order.
 */
export function getBoardDateGroupDefinitions(mode) {
  return [...(DATE_GROUPS_BY_MODE[mode] || DATE_GROUPS_BY_MODE.daily)];
}

/**
 * Returns the status field for a registered parent-board item type.
 * @param {string} itemType - Singular parent-board item type.
 * @returns {string} Status field name.
 */
export function getBoardStatusField(itemType) {
  return getBoardRegistryEntryByItemType(itemType)?.fields?.statusField ||
    "status";
}

function getConfigList(configKey) {
  return configKey ? appData?.config?.[configKey] || [] : [];
}

/**
 * Returns status config for a registered parent-board item type.
 * @param {string} itemType - Singular parent-board item type.
 * @returns {Array<object>} Status config entries.
 */
export function getBoardStatusConfig(itemType) {
  return getConfigList(
    getBoardRegistryEntryByItemType(itemType)?.fields?.statusConfigKey,
  );
}

/**
 * Returns sorted status category config for a registered parent-board item type.
 * @param {string} itemType - Singular parent-board item type.
 * @returns {Array<object>} Category config entries sorted by order.
 */
export function getBoardSortedCategories(itemType) {
  const categories = getConfigList(
    getBoardRegistryEntryByItemType(itemType)?.fields?.categoryConfigKey,
  );

  return [...categories].sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0));
}

/**
 * Returns type config for a registered parent-board item type.
 * @param {string} itemType - Singular parent-board item type.
 * @returns {Array<object>} Type config entries.
 */
export function getBoardTypeConfig(itemType) {
  return getConfigList(
    getBoardRegistryEntryByItemType(itemType)?.fields?.typeConfigKey,
  );
}

/**
 * Returns urgency config for a registered parent-board item type.
 * @param {string} itemType - Singular parent-board item type.
 * @returns {Array<object>} Priority config entries, or an empty list.
 */
export function getBoardUrgencyConfig(itemType) {
  return getConfigList(
    getBoardRegistryEntryByItemType(itemType)?.fields?.priorityConfigKey,
  );
}
